import { useState, useEffect } from 'react';
import { APIPath, baseURL } from 'shared/services/APIPath';

const useStudentEnrollmentData = ({ studentId, courseId }) => {
  const [students, setStudents] = useState([]);
  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Clear all state before fetching new data
    setStudents([]);
    setCourse(null);
    setEnrollments([]);
    setCourses([]);
    setBatches([]);
    setTutors([]);

    if (!studentId && !courseId) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let enrollmentData = [];
        let fetchedBatches = [];
        let fetchedCourses = [];
        let courseData = [];

        // --- Step 1: Fetch initial data based on the provided ID ---
        if (studentId) {
          const studentRes = await APIPath.get(baseURL + `/students/${studentId}`);
          setStudents([studentRes.data]);

          const enrollmentRes = await APIPath.get(baseURL + `/batch-enrollments?studentId.equals=${studentId}`);
          enrollmentData = enrollmentRes.data || [];
          setEnrollments(enrollmentData);

          // Get courses from enrollments (for student-centric view)
          courseData = enrollmentData.map(enrollment => enrollment.course).filter(Boolean);
          setCourses(courseData);

        } else if (courseId) {
          const [courseRes, batchesRes] = await Promise.all([
            APIPath.get(baseURL + `/courses/${courseId}`),
            APIPath.get(baseURL + `/batches?courseId.equals=${courseId}`),
          ]);

          courseData = [courseRes.data];
          setCourse(courseRes.data);
          setCourses(courseData);
          fetchedBatches = batchesRes.data || [];
          setBatches(fetchedBatches);

          if (fetchedBatches.length > 0) {
            const batchIdsList = fetchedBatches.map(b => b.id).join(',');
            const enrollmentRes = await APIPath.get(baseURL + `/batch-enrollments?batchId.in=${batchIdsList}`);
            enrollmentData = enrollmentRes.data || [];
          } else {
            enrollmentData = [];
          }
          
          setEnrollments(enrollmentData);
        }

        // --- Step 2: Extract unique IDs from the fetched enrollments ---
        const studentIds = new Set();
        const batchIds = new Set();
        const tutorIds = new Set();

        enrollmentData.forEach(enrollment => {
          if (enrollment.student?.id) studentIds.add(enrollment.student.id);
          if (enrollment.batch?.id) batchIds.add(enrollment.batch.id);
          if (enrollment.tutor?.id) tutorIds.add(enrollment.tutor.id);
        });

        // --- Step 3: Build filter strings and fetch all related entities in parallel ---
        const studentFilter = `id.in=${Array.from(studentIds).join(',')}`;
        const tutorFilter = `id.in=${Array.from(tutorIds).join(',')}`;

        const [studentRes, tutorRes] = await Promise.all([
          studentIds.size ? APIPath.get(baseURL + `/students?${studentFilter}`) : Promise.resolve({ data: [] }),
          tutorIds.size ? APIPath.get(baseURL + `/tutors?${tutorFilter}`) : Promise.resolve({ data: [] }),
        ]);

        const fetchedStudents = studentRes.data || [];
        const fetchedTutors = tutorRes.data || [];

        // Update state, handling the student-centric case where students were already fetched
        if (courseId) {
          setStudents(fetchedStudents);
        }
        if (studentId) {
          // This block now relies on enrollmentData
          const courseIds = new Set(enrollmentData.map(e => e.course?.id).filter(Boolean));
          const batchIdsFromEnrollment = new Set(enrollmentData.map(e => e.batch?.id).filter(Boolean));

          const [courseRes, batchRes] = await Promise.all([
            courseIds.size ? APIPath.get(baseURL + `/courses?id.in=${Array.from(courseIds).join(',')}`) : Promise.resolve({ data: [] }),
            batchIdsFromEnrollment.size ? APIPath.get(baseURL + `/batches?id.in=${Array.from(batchIdsFromEnrollment).join(',')}`) : Promise.resolve({ data: [] }),
          ]);

          fetchedCourses = courseRes.data || [];
          fetchedBatches = batchRes.data || [];
          setCourses(fetchedCourses);
          setBatches(fetchedBatches);
        }
        setTutors(fetchedTutors);

        // --- Step 4: Inject course details into batches for the final output ---
        const courseMap = new Map(courseData.map(c => [c.id, c]));
        const enrichedBatches = fetchedBatches.map(batch => ({
          ...batch,
          course: courseMap.get(batch.course.id) || null,
        }));
        setBatches(enrichedBatches);

      } catch (err) {
        console.error('Error fetching enrollment data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId, courseId]);

  return {
    students,
    course,
    enrollments,
    courses,
    batches,
    tutors,
    loading,
    error,
  };
};

export default useStudentEnrollmentData;
